/**
 * @fileoverview Central location-tracking store.
 * @description The single source of truth for the device's current location — read by
 * LocationGate (the first-load "enable location" prompt) and by the login request, which sends the
 * coordinates in its body when there are any. Location is
 * not mandatory for the whole session: the app only asks for it once, when it first loads. start()
 * (called once, by LocationGate on mount) checks the permission; if location isn't already on, it
 * opens the prompt and, at the same moment, asks the browser for a fix — so the browser's own
 * permission popup shows alongside ours. The prompt then only closes through confirmEnabled() (its
 * Enable button), which re-checks and does nothing until location really is on; it never asks the
 * browser again, so it can't bring up a second permission popup. Once the prompt has closed it
 * never reopens for the rest of this load: if location is turned off later, the store just drops
 * the coordinates (a login then goes out without them), and picks tracking back up quietly if it's
 * turned on again. Once there's a fix, watchLocation keeps it fresh in the background, so no
 * request waits on a GPS read.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { create } from 'zustand'

import {
  clearLocationWatch,
  queryGeolocationPermission,
  requestCurrentLocation,
  subscribeToPermissionChange,
  watchLocation,
} from '@/shared/lib/device/geolocation.utils'

import type { GeoBlockReason, GeoCoordinates } from '@/shared/lib/device/geolocation.utils'

export type LocationStatus = 'checking' | 'ready' | 'blocked'

/** Where the first-load prompt is: not decided yet, showing, or done for this load. */
export type LocationPromptState = 'pending' | 'open' | 'closed'

interface LocationState {
  status: LocationStatus
  reason: GeoBlockReason | null
  coordinates: GeoCoordinates | null
  prompt: LocationPromptState
  isConfirming: boolean
  start: () => void
  confirmEnabled: () => Promise<void>
  recheckPermission: () => void
}

const RETRY_INTERVAL_MS = 5000

let started = false
let watchId: number | null = null
let retryTimeoutId: ReturnType<typeof setTimeout> | null = null

/**
 * @description Stops the background watch and any pending retry, so at most one of each runs.
 */
function stopTracking(): void {
  if (watchId !== null) {
    clearLocationWatch(watchId)
    watchId = null
  }
  if (retryTimeoutId !== null) {
    clearTimeout(retryTimeoutId)
    retryTimeoutId = null
  }
}

/**
 * @description Zustand store for the device's location and the first-load prompt — see the file
 * header for the flow.
 * @returns {LocationState} The current location, the prompt state, and the store's actions.
 */
export const useLocationStore = create<LocationState>((set, get) => {
  /**
   * @description Clears the coordinates. Before the prompt has been decided, this is the "location
   * is off" signal that opens it; afterwards it changes nothing on screen.
   * @param {GeoBlockReason} reason - Why there's no location.
   */
  const markBlocked = (reason: GeoBlockReason) => {
    set((state) => ({
      status: 'blocked',
      reason,
      coordinates: null,
      prompt: state.prompt === 'pending' ? 'open' : state.prompt,
    }))
  }

  /**
   * @description Tries again later — but only while permission is granted (the location service
   * itself is off or unreachable). Retrying without permission could bring up the browser's popup
   * again, so permission changes are left to the permission listener instead.
   */
  const scheduleRetry = () => {
    retryTimeoutId = setTimeout(() => {
      retryTimeoutId = null
      void queryGeolocationPermission().then((state) => {
        if (state === 'granted') {
          void track()
        }
      })
    }, RETRY_INTERVAL_MS)
  }

  /**
   * @description Gets a fix and, on success, keeps it fresh in the background.
   * @returns {Promise<boolean>} True when a fix came back.
   */
  const track = async (): Promise<boolean> => {
    stopTracking()
    const result = await requestCurrentLocation()

    if (!result.ok) {
      markBlocked(result.reason)
      if (result.reason !== 'denied' && result.reason !== 'unsupported') {
        scheduleRetry()
      }
      return false
    }

    set({ status: 'ready', reason: null, coordinates: result.coordinates })
    watchId = watchLocation(
      (coordinates) => set({ status: 'ready', reason: null, coordinates }),
      () => {
        stopTracking()
        markBlocked('unavailable')
        scheduleRetry()
      },
    )
    return true
  }

  return {
    status: 'checking',
    reason: null,
    coordinates: null,
    prompt: 'pending',
    isConfirming: false,
    start: () => {
      if (started) {
        return
      }
      started = true

      subscribeToPermissionChange((state) => {
        if (state === 'granted' && get().status !== 'ready') {
          void track()
        } else if (state === 'denied') {
          stopTracking()
          markBlocked('denied')
        }
      })

      void queryGeolocationPermission().then((state) => {
        if (state !== 'granted') {
          set({ prompt: 'open' })
        }
        // Asked now, not from the prompt's button — so the browser's own popup shows with ours.
        void track()
      })
    },
    confirmEnabled: async () => {
      if (get().prompt !== 'open' || get().isConfirming) {
        return
      }
      if (get().status === 'ready') {
        set({ prompt: 'closed' })
        return
      }

      const permission = await queryGeolocationPermission()

      // Still not allowed: nothing to do until the visitor allows it in the browser.
      if (permission === 'prompt' || permission === 'denied') {
        return
      }

      set({ isConfirming: true })
      const enabled = await track()
      set(enabled ? { isConfirming: false, prompt: 'closed' } : { isConfirming: false })
    },
    recheckPermission: () => {
      if (get().status !== 'ready') {
        return
      }

      void queryGeolocationPermission().then((state) => {
        if (state === 'denied') {
          stopTracking()
          markBlocked('denied')
        }
      })
    },
  }
})

/**
 * @description Lets tests start the store afresh. Not for app code.
 */
export function resetLocationTrackingForTests(): void {
  stopTracking()
  started = false
}
